// ABOUTME: Radio input component with Radix UI RadioGroup primitives
// ABOUTME: Provides single radio, compound RadioGroup, and CRadioOption for consistent styling

import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { cn } from "@shared/lib/utils"

// Single Radio input (native HTML for simple use cases)
export interface CRadioProps extends React.InputHTMLAttributes<HTMLInputElement> {
  color?: "default" | "success" | "failure"
}

export const CRadio = React.forwardRef<HTMLInputElement, CRadioProps>(({ className, color, ...props }, ref) => {
  const colorClasses = {
    default: "text-gray-900 focus:ring-gray-400",
    success: "text-green-500 focus:ring-green-500",
    failure: "text-red-500 focus:ring-red-500",
  }

  return (
    <input
      type="radio"
      ref={ref}
      className={cn(
        "h-4 w-4 shrink-0 border-gray-300 bg-gray-100 focus:ring-2 accent-gray-900",
        color ? colorClasses[color] : colorClasses.default,
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  )
})

CRadio.displayName = "CRadio"

// CRadioOption - standardized radio with label (h-[30px], proper alignment)
export interface CRadioOptionProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "id"> {
  id: string
  label: React.ReactNode
  color?: "default" | "success" | "failure"
  wrapClassName?: string
}

export const CRadioOption = React.forwardRef<HTMLInputElement, CRadioOptionProps>(
  ({ id, label, className, wrapClassName, disabled, ...props }, ref) => {
    return (
      <div className={cn("flex items-center h-[30px]", wrapClassName)}>
        <CRadio
          ref={ref}
          id={id}
          disabled={disabled}
          className={cn(disabled ? "cursor-not-allowed" : "cursor-pointer", className)}
          {...props}
        />
        <label
          htmlFor={id}
          className={cn(
            "pl-2 text-sm font-medium leading-none text-gray-900 whitespace-nowrap",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          )}
        >
          {label}
        </label>
      </div>
    )
  },
)

CRadioOption.displayName = "CRadioOption"

// CRadioOptionsGroup - horizontal or vertical group of radio options
export interface CRadioOptionsGroupProps {
  children: React.ReactNode
  /** Layout orientation: horizontal, vertical, or responsive (vertical on mobile, horizontal on desktop) */
  orientation?: "horizontal" | "vertical" | "responsive"
  className?: string
  gap?: "sm" | "md" | "lg"
}

const gapClasses = {
  sm: "gap-2",
  md: "gap-4",
  lg: "gap-6",
}

// Responsive gaps - smaller on mobile (vertical), normal on desktop (horizontal)
const responsiveGapClasses = {
  sm: "gap-1 md:gap-2",
  md: "gap-2 md:gap-4",
  lg: "gap-3 md:gap-6",
}

export const CRadioOptionsGroup: React.FC<CRadioOptionsGroupProps> = ({
  children,
  orientation = "horizontal",
  className,
  gap = "md",
}) => {
  const orientationClasses = {
    horizontal: "flex-row",
    vertical: "flex-col",
    responsive: "flex-col md:flex-row",
  }

  // Use responsive gaps when orientation is responsive
  const gapClass = orientation === "responsive" ? responsiveGapClasses[gap] : gapClasses[gap]

  return <div className={cn("flex flex-wrap", orientationClasses[orientation], gapClass, className)}>{children}</div>
}

CRadioOptionsGroup.displayName = "CRadioOptionsGroup"

// RadioGroup compound component (Radix UI for complex use cases)
const RadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  )
})
RadioGroup.displayName = "RadioGroup"

const RadioGroupItem = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "aspect-square h-4 w-4 shrink-0 rounded-full border border-gray-300 bg-white data-[state=checked]:border-inverse data-[state=checked]:border-[5px] focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    />
  )
})
RadioGroupItem.displayName = "RadioGroupItem"

// CRadioGroupItem - Radix radio with label (styled like CRadioOption)
export interface CRadioGroupItemProps extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  label: React.ReactNode
  wrapClassName?: string
}

const CRadioGroupItem = React.forwardRef<React.ComponentRef<typeof RadioGroupPrimitive.Item>, CRadioGroupItemProps>(
  ({ label, className, wrapClassName, disabled, ...props }, ref) => {
    return (
      <label className={cn("flex items-center gap-2 cursor-pointer", disabled && "cursor-not-allowed", wrapClassName)}>
        <RadioGroupItem
          ref={ref}
          disabled={disabled}
          className={className}
          {...props}
        />
        <span className={cn("text-sm font-medium text-gray-900 whitespace-nowrap", disabled && "opacity-50")}>
          {label}
        </span>
      </label>
    )
  },
)
CRadioGroupItem.displayName = "CRadioGroupItem"

// Export compound components
export { RadioGroup, RadioGroupItem, CRadioGroupItem }
